import { compact } from '@lglp/utils';
import { is, prop, omit, test } from 'ramda';

import { getUrlFromDocument } from '../../../helpers/routes/mapping';
import { LinkType } from '../../../types';

/**
 * Clear the given object by removing lots of unecessary attributes
 * @param {any} props
 */
const clear = omit([
  'url',
  'href',
  'kind',
  'size',
  'name',
  'width',
  'height',
  'target',
]);

const getLinkTarget = ({ target, download }, mailto) => {
  if (is(String, target)) {
    return target;
  }

  if (download || mailto) {
    return null;
  }

  return '_blank';
};

/**
 * Function to get a link label from the given props (can be the title, text or a default value)
 * @param {any} props
 */
const getLinkLabel = ({ title, children }) => {
  if (is(String, title) && title.length > 0) {
    return title;
  }

  if (is(String, children) && children.length > 0) {
    return children;
  }

  return 'OuterLink';
};

/**
 * Function to format a link object to an object useable by the OuterLink component
 * @param {LinkWeb} props
 */
const formatWebLink = ({ url, ...props }) => {
  const link = clear(
    compact({
      ...props,
      to: url,
    })
  );

  if (test(/^(http|mailto)/, url) === true) {
    link.eventLabel = getLinkLabel(props);
    link.target = getLinkTarget(props, test(/^(mailto)/, url));
  }

  return link;
};

/**
 * Function to format a link object based on the given document
 * @param {LinkDocument} doc
 */
const formatDocLink = (doc) => ({
  to: getUrlFromDocument(doc),
});

/**
 * Try to guess the best format to use the given object as an OuterLink component.
 * @param {any} link
 */
const guess = (link) => {
  if (is(String, prop('to', link))) {
    return formatWebLink({ ...link, url: link.to });
  }

  if (is(String, prop('href', link))) {
    return formatWebLink({ ...link, url: link.href });
  }

  return formatWebLink({ ...link, url: '#' });
};

/**
 * @param {LinkDocument|LinkMedia|LinkWeb} l
 * @returns {({to: String}|*)}
 */
const formatLink = (l) => {
  switch (l.linkType) {
    case LinkType.LINK_TYPE_MEDIA:
    case LinkType.LINK_TYPE_WEB:
      return formatWebLink(l);
    case LinkType.LINK_TYPE_DOC:
      return formatDocLink(l);
    default:
      return guess(l);
  }
};

export default formatLink;
